import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge, Row, Col, Modal, Form, Table, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { KnowledgeArticle, KnowledgeArticleEditorEntry, User } from '../types';
import { knowledgeBaseApi, usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { API_URL } from '../config';
import { sanitizeRichTextHtml } from '../utils/richTextSanitizer';

const KnowledgeArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [showEditorsModal, setShowEditorsModal] = useState(false);
  const [editorSearch, setEditorSearch] = useState('');
  const [editorSearchLoading, setEditorSearchLoading] = useState(false);
  const [editorSearchResults, setEditorSearchResults] = useState<User[]>([]);
  const [articleEditors, setArticleEditors] = useState<KnowledgeArticleEditorEntry[]>([]);
  const [editorsModalLoading, setEditorsModalLoading] = useState(false);

  const fetchArticle = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await knowledgeBaseApi.getArticle(Number(id));
      setArticle(data);
    } catch (error: any) {
      console.error('Erro ao carregar artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar artigo', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [id, showAlert]);

  useEffect(() => {
    if (id) {
      fetchArticle();
    }
  }, [id, fetchArticle]);

  const handleToggleFavorite = async () => {
    if (!article) return;
    try {
      const result = await knowledgeBaseApi.toggleFavorite(article.id);
      setArticle({ ...article, is_favorite: result.is_favorite });
      showAlert({
        title: 'Sucesso',
        message: result.is_favorite ? 'Artigo adicionado aos favoritos' : 'Artigo removido dos favoritos',
        variant: 'success'
      });
    } catch (error: any) {
      console.error('Erro ao favoritar artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao favoritar artigo', variant: 'danger' });
    }
  };

  const handleDelete = async () => {
    if (!article) return;
    const ok = await showConfirm({
      title: 'Excluir artigo',
      message: 'Tem certeza que deseja excluir este artigo?',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await knowledgeBaseApi.deleteArticle(article.id);
      showAlert({ title: 'Sucesso', message: 'Artigo excluído com sucesso', variant: 'success' });
      navigate('/knowledge-base');
    } catch (error: any) {
      console.error('Erro ao excluir artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao excluir artigo', variant: 'danger' });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Processar HTML para substituir caminhos relativos de imagens por URLs completas
  const processArticleBody = (htmlContent: string): string => {
    if (!htmlContent) return '';

    // Garantir compatibilidade com imagens novas e URLs legadas salvas no body.
    const normalized = htmlContent.replace(/src="([^"]+)"/g, (_match, rawSrc) => {
      if (!rawSrc) return _match;
      if (rawSrc.startsWith('data:')) return _match;

      let normalizedSrc = rawSrc;

      // Se vier absoluto, tentar identificar e converter legados /uploads/knowledge-base/*.
      if (/^https?:\/\//i.test(normalizedSrc)) {
        try {
          const parsed = new URL(normalizedSrc);
          const legacyMatch = parsed.pathname.match(/^\/uploads\/knowledge-base\/([^/?#]+)$/);
          if (legacyMatch?.[1]) {
            normalizedSrc = `/api/knowledge-base/images/${legacyMatch[1]}`;
          } else {
            return _match;
          }
        } catch {
          return _match;
        }
      } else {
        const legacyRelativeMatch = normalizedSrc.match(/^\/uploads\/knowledge-base\/([^/?#]+)$/);
        if (legacyRelativeMatch?.[1]) {
          normalizedSrc = `/api/knowledge-base/images/${legacyRelativeMatch[1]}`;
        }
      }

      if (normalizedSrc.startsWith('/')) {
        normalizedSrc = `${API_URL}${normalizedSrc}`;
      }

      return `src="${normalizedSrc}"`;
    });
    return sanitizeRichTextHtml(normalized, {
      allowedTags: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'div', 'span',
        'strong', 'b', 'em', 'i', 'u', 's',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'a', 'img',
      ],
      allowImageSrc: (src) => {
        const value = src.trim().toLowerCase();
        return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:');
      },
    });
  };

  const canEdit =
    article &&
    (article.can_edit ??
      (article.created_by === user?.id || user?.role === 'admin'));
  const canDelete =
    article &&
    (article.can_delete ??
      (article.created_by === user?.id || user?.role === 'admin'));

  const loadArticleEditors = useCallback(async () => {
    if (!article || user?.role !== 'admin') return;
    try {
      setEditorsModalLoading(true);
      const list = await knowledgeBaseApi.listArticleEditors(article.id);
      setArticleEditors(list);
    } catch (e: any) {
      console.error(e);
      showAlert({ title: 'Erro', message: 'Não foi possível carregar editores do artigo.', variant: 'danger' });
    } finally {
      setEditorsModalLoading(false);
    }
  }, [article, user?.role, showAlert]);

  useEffect(() => {
    if (showEditorsModal && article && user?.role === 'admin') {
      loadArticleEditors();
    }
  }, [showEditorsModal, article, user?.role, loadArticleEditors]);

  const handleSearchEditors = async () => {
    const q = editorSearch.trim();
    if (q.length < 2) {
      showAlert({ title: 'Aviso', message: 'Digite pelo menos 2 caracteres para buscar.', variant: 'warning' });
      return;
    }
    try {
      setEditorSearchLoading(true);
      const res = await usersApi.search({ search: q, limit: 15 });
      setEditorSearchResults(res.items);
    } catch (e: any) {
      console.error(e);
      showAlert({ title: 'Erro', message: 'Erro ao buscar usuários.', variant: 'danger' });
    } finally {
      setEditorSearchLoading(false);
    }
  };

  const handleGrantEditor = async (userId: number) => {
    if (!article) return;
    try {
      await knowledgeBaseApi.grantArticleEditor(article.id, userId);
      showAlert({ title: 'Sucesso', message: 'Permissão de edição concedida.', variant: 'success' });
      await loadArticleEditors();
      const data = await knowledgeBaseApi.getArticle(article.id);
      setArticle(data);
      setEditorSearchResults([]);
    } catch (e: any) {
      console.error(e);
      showAlert({
        title: 'Erro',
        message: e.response?.data?.detail || 'Erro ao conceder permissão.',
        variant: 'danger',
      });
    }
  };

  const handleRevokeEditor = async (userId: number) => {
    if (!article) return;
    const ok = await showConfirm({
      title: 'Revogar edição',
      message: 'Remover permissão de edição deste usuário neste artigo?',
      variant: 'warning',
      confirmLabel: 'Revogar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await knowledgeBaseApi.revokeArticleEditor(article.id, userId);
      showAlert({ title: 'Sucesso', message: 'Permissão revogada.', variant: 'success' });
      await loadArticleEditors();
      const data = await knowledgeBaseApi.getArticle(article.id);
      setArticle(data);
    } catch (e: any) {
      console.error(e);
      showAlert({ title: 'Erro', message: 'Erro ao revogar permissão.', variant: 'danger' });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-muted">Artigo não encontrado.</p>
          <Button variant="primary" onClick={() => navigate('/knowledge-base')}>
            Voltar para Base de Conhecimento
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <Button variant="outline-secondary" onClick={() => navigate('/knowledge-base')}>
            ← Voltar
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="link"
            onClick={handleToggleFavorite}
            className="me-2"
            title={article.is_favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            {article.is_favorite ? '⭐ Favorito' : '☆ Favoritar'}
          </Button>
          {canEdit && (
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/knowledge-base/${article.id}/edit`)}
              className="me-2"
            >
              Editar
            </Button>
          )}
          {canDelete && (
            <Button variant="outline-danger" onClick={handleDelete}>
              Excluir
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button variant="outline-dark" className="ms-2" onClick={() => setShowEditorsModal(true)}>
              Editores (admin)
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <div className="mb-3">
            <Badge bg="secondary" className="me-2">
              {article.category?.name || 'Sem categoria'}
            </Badge>
          </div>
          <Card.Title className="mb-4">{article.title}</Card.Title>
          <div
            className="article-body csp-wave1-kb-article-body"
            dangerouslySetInnerHTML={{ __html: processArticleBody(article.body) }}
          />
          <hr className="my-4" />
          <div className="text-muted small">
            <p className="mb-0">
              <strong>Autor:</strong> {article.author_name || article.author?.name || 'Desconhecido'}
            </p>
            <p className="mb-0">
              <strong>Última atualização:</strong> {formatDate(article.updated_at)}
            </p>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showEditorsModal} onHide={() => setShowEditorsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editores do artigo (admin)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editorsModalLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover size="sm" className="mb-4">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Desde</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {articleEditors.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted text-center">
                      Nenhum editor extra (além do dono e permissões globais).
                    </td>
                  </tr>
                ) : (
                  articleEditors.map((ed) => (
                    <tr key={ed.user_id}>
                      <td>{ed.name}</td>
                      <td>{ed.email}</td>
                      <td>{new Date(ed.granted_at).toLocaleString('pt-BR')}</td>
                      <td>
                        <Button size="sm" variant="outline-danger" onClick={() => handleRevokeEditor(ed.user_id)}>
                          Revogar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}

          <h6 className="mb-2">Adicionar usuário</h6>
          <Row className="g-2 align-items-end mb-2">
            <Col md={8}>
              <Form.Label className="small text-muted">Buscar por nome ou e-mail</Form.Label>
              <Form.Control
                value={editorSearch}
                onChange={(e) => setEditorSearch(e.target.value)}
                placeholder="Ex.: Verônica"
              />
            </Col>
            <Col md={4}>
              <Button variant="primary" onClick={handleSearchEditors} disabled={editorSearchLoading} className="w-100">
                {editorSearchLoading ? <Spinner size="sm" animation="border" /> : 'Buscar'}
              </Button>
            </Col>
          </Row>
          {editorSearchResults.length > 0 && (
            <Table size="sm" bordered>
              <tbody>
                {editorSearchResults.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.name} <span className="text-muted small">({u.email})</span>
                    </td>
                    <td className="text-end csp-wave1-width-120">
                      <Button size="sm" variant="success" onClick={() => handleGrantEditor(u.id)}>
                        Conceder
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditorsModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KnowledgeArticleDetail;
